<template>
  <div class="rapport-stock-par-type position-relative">
    <div v-if="isLoading" class="rapport-loading-overlay">
      <SpinnerLoading />
    </div>

    <div v-if="infoBanniere" class="alert alert-info small mb-2">
      {{ infoBanniere }}
    </div>
    <div v-if="fetchError" class="alert alert-warning small mb-2">
      {{ fetchError }}
    </div>

    <div class="d-flex justify-content-end mb-2">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="!lignes.length || isLoading || isExporting"
        @click="exportPdf"
      >
        <i class="fas fa-file-export mr-1"></i>
        {{ isExporting ? "Impression en cours…" : "Imprimer" }}
      </button>
    </div>

    <div ref="rapportExportRoot" class="rapport-export-root">
      <div class="rapport-title-frame">
        RECAPITULATIF STOCK EN COURS PAR TYPE {{ periodeTitre }}
      </div>

      <div class="rapport-page border" :class="{ 'rapport-page--loading': isLoading }">
        <header class="rapport-header d-flex align-items-start justify-content-between flex-wrap">
          <div class="rapport-logo-block text-center">
          <img
            v-if="logoSrc"
            :src="logoSrc"
            alt=""
            class="rapport-logo-img"
          />
          <div v-else class="rapport-logo-fallback">
            <i class="fa-solid fa-mortar-pestle"></i>
          </div>
        </div>
        <div class="rapport-pharma-name text-uppercase text-center flex-grow-1 px-2">
          {{ nomPharmacie }}
        </div>
        <div class="rapport-meta text-right small">
          <div>{{ dateEditionLabel }}</div>
          <div>{{ pageLabel }}</div>
        </div>
        </header>

        <div class="table-responsive">
        <table class="table table-sm table-bordered mb-0 rapport-grid">
          <thead>
            <tr class="rapport-stock-row-gray">
              <th>Type Actuel</th>
              <th class="text-right">Marge</th>
              <th class="text-right">% TVA</th>
              <th class="text-right">Prix PUBLIC TTC</th>
              <th class="text-right">Prix Vente HT</th>
              <th class="text-right">Prix Achat TTC</th>
              <th class="text-right">Prix Achat HT</th>
              <th class="text-right">Prix Achat Selon Marge TTC</th>
              <th class="text-right">Prix Achat Selon Marge HT</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="!lignes.length">
              <tr>
                <td colspan="9" class="text-center text-muted py-4">
                  Aucune ligne à afficher pour cette période.
                </td>
              </tr>
            </template>
            <template v-else>
              <tr v-for="(row, idx) in lignes" :key="idx" :class="stockBodyRowClass(idx)">
                <td>{{ row.type_actuel }}</td>
                <td class="text-right">{{ formatCoef(row.marge_coef) }}</td>
                <td class="text-right">{{ formatTvaPct(row.type_actuel) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_public_ttc) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_vente_ht) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_achat_ttc) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_achat_ht) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_achat_selon_marge_ttc) }}</td>
                <td class="text-right">{{ formatMoney(row.prix_achat_selon_marge_ht) }}</td>
              </tr>
            </template>
          </tbody>
          <tfoot v-if="hasTotaux">
            <tr class="rapport-totaux">
              <td class="font-weight-bold">TOTAUX</td>
              <td class="text-right">—</td>
              <td class="text-right">—</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_public_ttc) }}</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_vente_ht) }}</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_achat_ttc) }}</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_achat_ht) }}</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_achat_selon_marge_ttc) }}</td>
              <td class="text-right font-weight-bold">{{ formatMoney(totaux.prix_achat_selon_marge_ht) }}</td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerLoading from "@/components/SpinnerLoading.vue";
import logoClementine from "@/assets/logo_clementine.png";
import { overrideMargeParType } from "@/utils/rapportStockHelper";

/**
 * Récap « stock par type » (PDF). API : GET /api/statistiques/rapport-stock
 * Réponse : data.periodes[].lignes (ou data.lignes en ancien format).
 */
export default {
  name: "RapportStockParType",
  components: { SpinnerLoading },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    period: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      fetchError: null,
      infoBanniere: null,
      dateAuIso: "",
      lignes: [],
      totaux: null,
      tauxTvaUtilise: 0.18,
      nombreProduitsConsidere: null,
      pageCourante: 1,
      pageTotal: 1,
      fetchSeq: 0,
      isExporting: false,
    };
  },
  computed: {
    nomPharmacie() {
      const p = this.$store.state.pharmacie;
      return (p && p.nom_pharmacie) || "PHARMACIE";
    },
    logoSrc() {
      const p = this.$store.state.pharmacie;
      if (!p) return logoClementine;
      return p.logo_url || p.logo || logoClementine;
    },
    dateAuLabel() {
      return this.formatFrDate(this.dateAuIso) || this.formatFrDate(this.inferredDateAu()) || "—";
    },
    dateEditionLabel() {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    pageLabel() {
      return `${this.pageCourante} / ${this.pageTotal || 1}`;
    },
    hasTotaux() {
      return this.totaux && typeof this.totaux === "object";
    },
    periodeTitre() {
      const f = this.filters || {};
      const du = this.formatFrDate(String(f.period_from || "").slice(0, 10));
      const au = this.formatFrDate(String(f.period_to || f.period_from || "").slice(0, 10));
      if (du && au && du !== au) return `DU ${du} AU ${au}`;
      if (au) return `AU ${au}`;
      if (du) return `AU ${du}`;
      const fallback = this.dateAuLabel;
      return fallback && fallback !== "—" ? `AU ${fallback}` : "";
    },
    exportFilename() {
      const f = this.filters || {};
      const from = String(f.period_from || "debut").slice(0, 10);
      const to = String(f.period_to || from).slice(0, 10);
      return `rapport-stock_${from}_${to}.pdf`;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.tryFetchRapport();
      },
    },
    period() {
      this.tryFetchRapport();
    },
  },
  mounted() {
    this.tryFetchRapport();
  },
  methods: {
    updateChart() {
      this.tryFetchRapport();
    },
    hasDateFilters() {
      const f = this.filters || {};
      const du = f.period_from || f.debut;
      const au = f.period_to || f.fin;
      return Boolean(du && au);
    },
    tryFetchRapport() {
      if (!this.hasDateFilters()) {
        this.fetchError =
          "Sélectionnez une période (début et fin) puis cliquez sur Valider pour charger le rapport.";
        this.applyEmptyState();
        this.dateAuIso = this.inferredDateAu();
        return;
      }
      this.fetchRapport();
    },
    extractRapportRoot(response) {
      const body = response && response.data !== undefined ? response.data : response;
      if (!body || typeof body !== "object") return null;
      if (body.data !== undefined && body.data !== null && typeof body.data === "object") {
        return body.data;
      }
      return body;
    },
    resolvePeriodes(root) {
      if (!root || typeof root !== "object") return [];
      if (Array.isArray(root.periodes) && root.periodes.length) {
        return root.periodes;
      }
      const lignes = root.lignes ?? root.rows ?? root.lines;
      if (Array.isArray(lignes) && lignes.length) {
        return [
          {
            periode: root.au ?? root.period_to ?? root.period_from,
            period_from: root.period_from,
            period_to: root.period_to,
            nombre_produits_considere: root.nombre_produits_considere,
            lignes,
          },
        ];
      }
      return [];
    },
    extractLignesBrutes(root) {
      const periodes = this.resolvePeriodes(root);
      if (!periodes.length) return [];

      if (periodes.length === 1) {
        const lignes = periodes[0].lignes ?? periodes[0].rows ?? [];
        return Array.isArray(lignes) ? lignes : [];
      }

      const byCategorie = new Map();
      const sumKeys = [
        "prix_de_vente",
        "ht",
        "prix_achat",
        "prix_achat_ht",
        "prix_achat_selon_coef_ttc",
        "prix_achat_selon_coef_ht",
      ];

      periodes.forEach((periode) => {
        const lignes = Array.isArray(periode.lignes) ? periode.lignes : [];
        lignes.forEach((row) => {
          const key =
            row.categorie_du_produit ??
            row.type_actuel ??
            row.type ??
            row.rayon_du_produit ??
            "—";
          if (!byCategorie.has(key)) {
            byCategorie.set(key, { ...row, categorie_du_produit: key });
            return;
          }
          const acc = byCategorie.get(key);
          sumKeys.forEach((k) => {
            acc[k] = (Number(acc[k]) || 0) + (Number(row[k]) || 0);
          });
        });
      });

      return Array.from(byCategorie.values());
    },
    buildRapportUrl() {
      const url = `/api/statistiques/rapport-stock`;
      const params = new URLSearchParams();
      const f = this.filters || {};

      if (this.period) {
        const typePeriode = this.period === "MENSUEL" ? "MENSUELLE" : String(this.period);
        params.append("type_periode", typePeriode);
      }
      const du = String(f.period_from || f.debut || "").slice(0, 10);
      const au = String(f.period_to || f.fin || "").slice(0, 10);
      if (du) {
        params.append("period_from", du);
      }
      if (au) {
        params.append("period_to", au);
      }

      const qs = params.toString();
      return qs ? `${url}?${qs}` : url;
    },
    inferredDateAu() {
      const f = this.filters || {};
      if (f.period_to) return String(f.period_to).slice(0, 10);
      if (f.fin) return String(f.fin).slice(0, 10);
      if (f.debut) return String(f.debut).slice(0, 10);
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    formatFrDate(iso) {
      if (!iso || typeof iso !== "string") return "";
      if (iso.includes("/")) return iso.trim();
      const part = iso.slice(0, 10);
      const m = part.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return "";
      return `${m[3]}/${m[2]}/${m[1]}`;
    },
    formatCoef(val) {
      if (val === null || val === undefined || val === "") return "—";
      const n = Number(val);
      if (Number.isNaN(n)) return "—";
      const s = n.toFixed(4).replace(/\.?0+$/, "");
      return s;
    },
    isParapharmacie(typeActuel) {
      const key = String(typeActuel || "")
        .trim()
        .toLocaleUpperCase("fr")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return key === "PARAPHARMACIE";
    },
    formatTvaPct(typeActuel) {
      const pct = this.isParapharmacie(typeActuel) ? 18 : 0;
      return pct.toFixed(2).replace(".", ",");
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === "") return "—";
      if (typeof this.$moneyFormat === "function") return this.$moneyFormat(val);
      return String(val);
    },
    stockBodyRowClass(idx) {
      return idx % 2 === 0 ? "rapport-stock-row-yellow" : "rapport-stock-row-blue";
    },
    normalizeRow(raw) {
      const tvaApplicable =
        raw.tva_du_produit !== undefined
          ? Boolean(raw.tva_du_produit)
          : raw.tva_applicable !== undefined
            ? Boolean(raw.tva_applicable)
            : raw.tva_pct != null
              ? Number(raw.tva_pct) > 0
              : null;

      return {
        type_actuel:
          raw.categorie_du_produit ??
          raw.type_actuel ??
          raw.Type_actuel ??
          raw.type ??
          "—",
        marge_coef: overrideMargeParType(
        raw.categorie_du_produit ?? raw.type_actuel ?? raw.Type_actuel ?? raw.type ?? "",
        raw.coef_conversion_de_prix_vente_achat ??
          raw.marge_coef ??
          raw.marge_pct ??
          raw.marge ??
          raw.Marge
      ),
        tva_applicable: tvaApplicable,
        prix_public_ttc: raw.prix_de_vente ?? raw.prix_public_ttc ?? raw.Prix_PUBLIC_TTC,
        prix_vente_ht: raw.ht ?? raw.prix_vente_ht ?? raw.Prix_Vente_HT,
        prix_achat_ttc: raw.prix_achat ?? raw.prix_achat_ttc ?? raw.Prix_Achat_TTC,
        prix_achat_ht: raw.prix_achat_ht ?? raw.Prix_Achat_HT,
        prix_achat_selon_marge_ttc:
          raw.prix_achat_selon_coef_ttc ??
          raw.prix_achat_selon_marge_ttc ??
          raw.Prix_Achat_selon_Marge_TTC,
        prix_achat_selon_marge_ht:
          raw.prix_achat_selon_coef_ht ??
          raw.prix_achat_selon_marge_ht ??
          raw.Prix_Achat_selon_Marge_HT,
      };
    },
    computeTotauxFromLignes(lignes) {
      const keys = [
        "prix_public_ttc",
        "prix_vente_ht",
        "prix_achat_ttc",
        "prix_achat_ht",
        "prix_achat_selon_marge_ttc",
        "prix_achat_selon_marge_ht",
      ];
      const totaux = {};
      keys.forEach((k) => {
        totaux[k] = lignes.reduce((sum, row) => sum + (Number(row[k]) || 0), 0);
      });
      return totaux;
    },
    applyRapportRoot(root, auHint) {
      const periodes = this.resolvePeriodes(root);
      const lastPeriode = periodes.length ? periodes[periodes.length - 1] : null;

      this.dateAuIso =
        root.period_to ??
        root.au ??
        (lastPeriode && (lastPeriode.period_to || lastPeriode.periode)) ??
        auHint ??
        this.inferredDateAu();

      if (root.taux_tva_utilise != null) {
        this.tauxTvaUtilise = Number(root.taux_tva_utilise);
      }

      this.nombreProduitsConsidere =
        root.nombre_produits_considere ??
        (lastPeriode && lastPeriode.nombre_produits_considere) ??
        root.nombre_produits ??
        null;

      const lignesBrutes = this.extractLignesBrutes(root);
      this.lignes = lignesBrutes.map((r) => this.normalizeRow(r));
      this.totaux = root.totaux || root.total || this.computeTotauxFromLignes(this.lignes);
      this.pageCourante = root.page || 1;
      this.pageTotal = root.page_total || root.pages || 1;
    },
    applyEmptyState() {
      this.lignes = [];
      this.totaux = null;
      this.pageCourante = 1;
      this.pageTotal = 1;
    },
    async fetchRapport() {
      const fetchId = ++this.fetchSeq;
      this.isLoading = true;
      this.fetchError = null;
      this.infoBanniere = null;

      const f = this.filters || {};
      const auHint = f.period_to || f.fin || f.debut;

      try {
        const response = await this.axios.get(this.buildRapportUrl());
        if (fetchId !== this.fetchSeq) return;

        const root = this.extractRapportRoot(response);
        const lignesBrutes = this.extractLignesBrutes(root);

        if (lignesBrutes.length > 0) {
          this.applyRapportRoot(root, auHint);
          return;
        }

        this.applyEmptyState();
        this.dateAuIso = auHint ? String(auHint).slice(0, 10) : this.inferredDateAu();
        this.fetchError = "Aucune donnée disponible pour cette période.";
      } catch (e) {
        if (fetchId !== this.fetchSeq) return;
        console.error("RapportStockParType rapport-stock:", e);
        this.applyEmptyState();
        this.dateAuIso = auHint ? String(auHint).slice(0, 10) : this.inferredDateAu();
        this.fetchError =
          "Impossible de charger le rapport de stock. Vérifiez l'API /api/statistiques/rapport-stock.";
      } finally {
        if (fetchId === this.fetchSeq) {
          this.isLoading = false;
        }
      }
    },
    buildPdfFooter() {
      const p = this.$store.state.pharmacie;
      if (!p) return { line1: "", line2: "" };
      const parts1 = [p.nom_pharmacie, p.adresse, p.telephone1 ? `Tél: ${p.telephone1}` : ""].filter(Boolean);
      const parts2 = [p.rccm ? `RCCM: ${p.rccm}` : "", p.nif ? `NIF: ${p.nif}` : "", p.email || ""].filter(Boolean);
      return { line1: parts1.join(" | "), line2: parts2.join(" | ") };
    },
    async exportPdf() {
      if (!this.lignes.length || this.isExporting) return;
      const el = this.$refs.rapportExportRoot;
      if (!el) return;

      this.isExporting = true;
      try {
        await this.$nextTick();
        this.printElement(el, `Rapport de stock ${this.exportFilename}`);
      } catch (e) {
        console.error("Impression rapport stock:", e);
      } finally {
        this.isExporting = false;
      }
    },

    printElement(el, title) {
      const printWindow = window.open("", "_blank");
      if (!printWindow) return;
      const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
        .map((node) => node.outerHTML)
        .join("\n");
      printWindow.document.write(`<!doctype html><html><head><title>${title}</title>${styles}</head><body>${el.outerHTML}</body></html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
      };
    },
  },
};
</script>

<style scoped>
.rapport-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.rapport-page--loading {
  pointer-events: none;
  opacity: 0.55;
}

.rapport-export-root {
  background: #fff;
}

.rapport-title-frame {
  border: 2px solid #333;
  border-bottom: none;
  padding: 10px 14px;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  background: #b39ddb;
  color: #111;
}

.rapport-export-root .rapport-page {
  border-top: none;
}

.rapport-page {
  background: #fff;
  color: #111;
  font-size: 12px;
}

.rapport-header {
  padding: 10px 12px 6px;
  border-bottom: 1px solid #ccc;
}

.rapport-logo-block {
  min-width: 56px;
}

.rapport-logo-img {
  max-height: 52px;
  max-width: 72px;
  object-fit: contain;
}

.rapport-logo-fallback {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e8f5e9;
  color: #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto;
}

.rapport-pharma-name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  align-self: center;
}

.rapport-meta {
  min-width: 88px;
  color: #333;
}

.rapport-grid thead th {
  font-weight: 700;
  font-size: 11px;
  vertical-align: middle;
  white-space: nowrap;
}

.rapport-stock-row-gray th,
.rapport-stock-row-gray td {
  background: #ececec !important;
}

.rapport-grid tbody td {
  font-size: 11px;
}

.rapport-stock-row-gray td {
  background: #ececec !important;
}

.rapport-stock-row-yellow td {
  background: #fffde7 !important;
}

.rapport-stock-row-blue td {
  background: #e3f2fd !important;
}

.rapport-totaux td {
  background: #ececec !important;
  border-top: 2px solid #666 !important;
  font-size: 11px;
}

.rapport-grid th,
.rapport-grid td {
  border-color: #999 !important;
  padding: 6px 8px;
}
</style>
